import { BookOpen, HelpCircle, X } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { helpArticle, helpTitle, isHelpTopic, type HelpTopic } from "../help/index.js";
import { DOCK_PANEL, useDockTarget } from "./dock.js";
import { Markdown } from "./markdown.js";
import { InfoPopover, Sheet } from "./overlay.js";

interface HelpContext {
  open: (topic: HelpTopic) => void;
}

const Context = createContext<HelpContext>({ open: () => {} });

export const useHelp = () => useContext(Context);

/** Hosts the help drawer; any component can open an article with `useHelp().open(topic)`. */
export function HelpProvider({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const [topic, setTopic] = useState<HelpTopic | null>(null);
  const open = useCallback((next: HelpTopic) => setTopic(next), []);
  const value = useMemo(() => ({ open }), [open]);
  const dock = useDockTarget();
  const source = topic ? helpArticle(topic, i18n.language) : "";
  // The first heading becomes the drawer title, the rest is the body.
  const body = source.replace(/^#\s+.+\n?/, "");
  const title = helpTitle(source) || t("help.title");
  const article = (
    <Markdown
      source={body}
      helpLink={(target, label, key) =>
        isHelpTopic(target) ? (
          <button
            key={key}
            type="button"
            onClick={() => setTopic(target)}
            className="text-accent underline underline-offset-2"
          >
            {label}
          </button>
        ) : (
          label
        )
      }
    />
  );
  return (
    <Context.Provider value={value}>
      {children}
      {dock ? (
        topic !== null &&
        createPortal(
          <HelpPanel title={title} onClose={() => setTopic(null)}>
            {article}
          </HelpPanel>,
          dock,
        )
      ) : (
        <Sheet
          open={topic !== null}
          onOpenChange={(isOpen) => !isOpen && setTopic(null)}
          title={title}
        >
          {article}
        </Sheet>
      )}
    </Context.Provider>
  );
}

/**
 * Help in the right block (wide screens, docs/web.md "Layout"): beside the
 * page rather than over it, so the page stays usable while reading. Escape
 * or ✕ closes it; it takes the focus when it opens.
 */
function HelpPanel({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const close = useRef<HTMLButtonElement>(null);
  useEffect(() => close.current?.focus(), []);
  return (
    <aside
      aria-label={title}
      onKeyDown={(event) => {
        if (event.key === "Escape") onClose();
      }}
      className={`flex w-96 flex-col ${DOCK_PANEL}`}
    >
      <div className="flex items-center justify-between gap-4 border-b border-line px-5 py-4">
        <h2 className="text-base font-semibold">{title}</h2>
        <button
          ref={close}
          type="button"
          onClick={onClose}
          className="rounded-md p-1 text-muted hover:bg-surface-2 hover:text-text"
          aria-label={t("common.close")}
        >
          <X className="size-5" aria-hidden />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
    </aside>
  );
}

/** "How it works" link that opens a help article. */
export function HelpLink({ topic, children }: { topic: HelpTopic; children?: ReactNode }) {
  const { t } = useTranslation();
  const { open } = useHelp();
  return (
    <button
      type="button"
      onClick={() => open(topic)}
      className="inline-flex min-h-6 items-center gap-1 text-sm text-accent hover:underline"
    >
      <BookOpen className="size-4" aria-hidden />
      {children ?? t("help.howItWorks")}
    </button>
  );
}

/** "?" next to a label: a short explanation, with a link to the full article when given. */
export function FieldHint({ text, topic }: { text: string; topic?: HelpTopic }) {
  const { t } = useTranslation();
  const { open } = useHelp();
  return (
    <InfoPopover
      trigger={
        <button
          type="button"
          className="-m-1 inline-flex rounded-full p-1 text-muted hover:text-text"
          aria-label={t("help.more")}
        >
          <HelpCircle className="size-4" aria-hidden />
        </button>
      }
    >
      <p>{text}</p>
      {topic && (
        <button
          type="button"
          onClick={() => open(topic)}
          className="mt-1 text-xs text-accent hover:underline"
        >
          {t("help.readMore")}
        </button>
      )}
    </InfoPopover>
  );
}
